import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {ResponsiveValue} from '@primer/react'
import {Avatar, type AvatarProps} from '@primer/styled-react'
import {useMemo} from 'react'

export interface GitHubAvatarProps extends AvatarProps {
  /** Sets the width and height of the avatar. */
  size?: number | ResponsiveValue<number>
  /** Sets the shape of the avatar to a square if true. If false, the avatar will be circular. */
  square?: boolean
  /** URL of the avatar image. */
  src: string
  /** Provide alt text when the Avatar is used without the user's name next to it. */
  alt?: string
  /** Additional class name. */
  className?: string
  ref?: React.Ref<HTMLImageElement>
}

export function GitHubAvatar({ref, src, size = 20, sx, ...otherProps}: GitHubAvatarProps) {
  const avatarUrl = useMemo(() => {
    // src maybe be relative, e.g. `/mona.png`, in which case the instances main origin should be used,
    // e.g. "https://github.com" for dotcom. If src is a fully quallified URL then the origin argument is ignored.
    const url = new URL(src, ssrSafeLocation.origin)

    // do not override size if it's already set as query parameter
    if (!url.searchParams.has('size') && !url.searchParams.has('s')) {
      // we double size for better rendering on high density pixel screens
      url.searchParams.set('size', String(Number(size) * 2))
    }

    return url.toString()
  }, [src, size])

  // eslint-disable-next-line @github-ui/github-monorepo/no-sx, @github-ui/github-monorepo/no-sx-components
  return <Avatar src={avatarUrl} size={size} data-testid="github-avatar" sx={sx} {...otherProps} ref={ref} />
}

try{ GitHubAvatar.displayName ||= 'GitHubAvatar' } catch {}